// App.js
import React, { useState } from 'react';
import SearchBar from './components/SearchBar';
import QuantumToggle from './components/QuantumToggle';
import MovieSidebar from './components/MovieSidebar';
import OverviewTab from './components/tabs/OverviewTab';
import CastTab from './components/tabs/CastTab';
import SoundtrackTab from './components/tabs/SoundtrackTab';
import BoxOfficeTab from './components/tabs/BoxOfficeTab';
import DialoguesTab from './components/tabs/DialoguesTab';
import TrailerTab from './components/tabs/TrailerTab';
import { searchMovies } from './services/api';
import InfoPanel from './components/InfoPanel';
import BrandTitle from './components/BrandTitle';
import FilmReelIcon from './components/FilmReelIcon';
import TechStackPanel from './components/TechStackPanel'; 
import './components/InfoPanel.css';
import './App.css';

function App() {
  const [useQuantum, setUseQuantum] = useState(false);
  const [movie, setMovie] = useState(null);
  const [similarMovies, setSimilarMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTime, setSearchTime] = useState(null);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // Add this state

  const handleSearch = async (query) => {
    setSearchQuery(query); // Update the search query
    setLoading(true);
    setError(null);
    try {
      const startTime = performance.now();
      const response = await searchMovies(query, useQuantum);
      const endTime = performance.now();
      
      setMovie(response.movie);
      setSimilarMovies(response.similar_movies || []);
      setSearchTime((endTime - startTime).toFixed(2));
    } catch (error) {
      console.error('Search failed:', error);
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSimilarMovieClick = async (selectedMovie) => {
    if (!selectedMovie || !selectedMovie.id) {
      console.error('Invalid movie object:', selectedMovie);
      return;
    }

    console.log('Handling click for movie:', selectedMovie.title);
    
    // Update the search bar with the selected movie title
    setSearchQuery(selectedMovie.title);
    
    setLoading(true);
    setError(null);
    
    try {
      // First try to fetch by ID if available
      let response;
      if (selectedMovie.id) {
        response = await searchMovies(selectedMovie.title, useQuantum);
      } else {
        response = await searchMovies(selectedMovie.title, useQuantum);
      }
      
      if (response && response.movie) {
        setMovie(response.movie);
        setSimilarMovies(response.similar_movies || []);
        // Reset to overview tab when changing movies
        setActiveTab('overview');
      } else {
        throw new Error('No movie data returned');
      }
    } catch (error) {
      console.error('Failed to load similar movie:', error);
      setError('Failed to load movie details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Add a handler for search query changes
  const handleSearchQueryChange = (query) => {
    setSearchQuery(query);
  };

  return (
    <div className="app-container">
      <InfoPanel />
      <div className="app-main">
        <header className="app-header">
          <div className="header-content">
            <BrandTitle />
            <div className="search-container">
              <SearchBar 
                onSearch={handleSearch} 
                value={searchQuery} 
                onChange={handleSearchQueryChange} 
              />
              <QuantumToggle 
                useQuantum={useQuantum} 
                onToggle={() => setUseQuantum(!useQuantum)} 
              />
            </div>
          </div>
        </header>
        
        {loading ? (
          <div className="loading">
            <h2>Searching with {useQuantum ? 'Quantum' : 'Classical'} Algorithm...</h2>
            <div className="spinner"></div>
          </div>
        ) : error ? (
          <div className="error-state">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={() => setError(null)}>Try Again</button>
          </div>
        ) : movie ? (
          <>
            <main className="content-area">
              <div className="main-content">
                <div className="search-info">
                  {searchTime && (
                    <p>Search completed in {searchTime} ms using {useQuantum ? 'Quantum' : 'Classical'} algorithm</p>
                  )}
                </div>
                
                <div className="tabs">
                  <button 
                    className={activeTab === 'overview' ? 'active' : ''} 
                    onClick={() => setActiveTab('overview')}
                  >
                    Overview
                  </button>
                  <button 
                    className={activeTab === 'cast' ? 'active' : ''} 
                    onClick={() => setActiveTab('cast')}
                  >
                    Cast & Crew
                  </button>
                  <button 
                    className={activeTab === 'soundtrack' ? 'active' : ''} 
                    onClick={() => setActiveTab('soundtrack')}
                  >
                    Soundtrack
                  </button>
                  <button 
                    className={activeTab === 'boxoffice' ? 'active' : ''} 
                    onClick={() => setActiveTab('boxoffice')}
                  >
                    Box Office
                  </button>
                  <button 
                    className={activeTab === 'dialogues' ? 'active' : ''} 
                    onClick={() => setActiveTab('dialogues')}
                  >
                    Dialogues
                  </button>
                  <button 
                    className={activeTab === 'trailers' ? 'active' : ''} 
                    onClick={() => setActiveTab('trailers')}
                  >
                    Trailers
                  </button>
                </div>
                
                <div className="tab-content">
                  {activeTab === 'overview' && <OverviewTab movie={movie} />}
                  {activeTab === 'cast' && <CastTab movie={movie} />}
                  {activeTab === 'soundtrack' && <SoundtrackTab movie={movie} />}
                  {activeTab === 'boxoffice' && <BoxOfficeTab movie={movie} />}
                  {activeTab === 'dialogues' && <DialoguesTab movie={movie} />}
                  {activeTab === 'trailers' && <TrailerTab movie={movie} />}
                </div>
              </div>
              
              <MovieSidebar 
                movies={similarMovies} 
                onMovieClick={handleSimilarMovieClick} 
              />
            </main>
            <footer>
              <TechStackPanel />
            </footer>
          </>
        ) : (
          <>
            <div className="empty-state">
              <h2>Search for a movie to get started</h2>
              <p>Toggle quantum search to compare performance with classical algorithms</p>
            </div>
            <footer>
              <TechStackPanel />
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

export default App;